import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@v2/components/ui/select";
import { Label } from "@v2/components/ui/label";
import { SUPPORTED_LANGS, LANG_LABELS, type SupportedLang, isSupportedLang } from "../lib/i18n";
import { supabaseV2 } from "../lib/supabase";

// Selettore lingua riusabile. Cambia subito la UI via i18next, scrive
// in localStorage (gestito dal detector), e se l'utente e' loggato
// persiste anche in auth.users.raw_user_meta_data.lang per
// sincronizzazione cross-device.

export function LangSwitcher({ compact = false }: { compact?: boolean }) {
  const { i18n, t } = useTranslation();
  const [current, setCurrent] = useState<SupportedLang>(() => {
    const lang = (i18n.language || "it").toLowerCase().split("-")[0];
    return isSupportedLang(lang) ? lang : "it";
  });

  useEffect(() => {
    const onChange = (lng: string) => {
      const lang = (lng || "").toLowerCase().split("-")[0];
      if (isSupportedLang(lang)) setCurrent(lang);
    };
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, [i18n]);

  const change = async (next: string) => {
    if (!isSupportedLang(next)) return;
    await i18n.changeLanguage(next);
    try {
      const { data } = await supabaseV2.auth.getUser();
      const meta = data.user?.user_metadata || {};
      if (meta.lang !== next) {
        await supabaseV2.auth.updateUser({ data: { ...meta, lang: next } });
      }
    } catch {
      // se utente non loggato o errore di rete, basta localStorage
    }
  };

  return (
    <div className={compact ? "inline-flex items-center gap-2" : "space-y-1"}>
      {!compact && <Label className="text-xs">{t("lang.switcherLabel")}</Label>}
      <Select value={current} onValueChange={change}>
        <SelectTrigger className={compact ? "h-7 w-[120px] text-xs" : "h-8 w-[160px] text-sm"}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {SUPPORTED_LANGS.map((lang) => (
            <SelectItem key={lang} value={lang}>
              {LANG_LABELS[lang]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
